import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';
import { TRIGGER_TYPES, ACTION_TYPES } from './nodeTypes';

function DraggableCard({ nodeType }) {
  const onDragStart = (event) => {
    event.dataTransfer.setData('application/reactflow', nodeType.type);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
    >
      <div
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: nodeType.color }}
      />
      <span className="text-[12px] font-medium text-gray-700 leading-tight truncate">
        {nodeType.label}
      </span>
    </div>
  );
}

export default function NodePaletteModal({ category, anchorEl, onClose }) {
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(false);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  // Animate in
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        anchorEl && !anchorEl.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, anchorEl]);

  // Focus search on open
  useEffect(() => {
    if (searchRef.current) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, []);

  // Reset search when category changes
  useEffect(() => {
    setSearch('');
  }, [category]);

  const nodes = category === 'trigger' ? TRIGGER_TYPES : ACTION_TYPES;
  const query = search.toLowerCase();

  const filteredGroups = useMemo(() => {
    const filtered = nodes.filter((n) =>
      !n.hidden && (!query || n.label.toLowerCase().includes(query) || (n.subcategory || '').toLowerCase().includes(query))
    );

    if (category === 'trigger') {
      return [{ name: null, items: filtered }];
    }

    const groups = {};
    for (const node of filtered) {
      const sub = node.subcategory || 'Other';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(node);
    }

    const order = ['CRM', 'AI', 'Prospecting', 'Google', 'Communication', 'Web', 'Logic', 'Other'];
    return order
      .filter((name) => groups[name])
      .map((name) => ({ name, items: groups[name] }));
  }, [nodes, query, category]);

  // Position the flyout to the right of the nav bar
  const style = {
    position: 'fixed',
    top: anchorEl ? anchorEl.getBoundingClientRect().top - 8 : 0,
    left: anchorEl ? anchorEl.getBoundingClientRect().right + 12 : 80,
    zIndex: 50,
  };

  return createPortal(
    <div
      ref={panelRef}
      style={style}
      className={`w-56 max-h-[70vh] bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col overflow-hidden transition-all duration-150 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
      }`}
    >
      {/* Search */}
      <div className="px-3 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-300 transition-colors bg-gray-50/50"
          />
        </div>
      </div>

      {/* Node list */}
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {filteredGroups.length === 0 && (
          <p className="text-[12px] text-gray-400 text-center py-6">No nodes found</p>
        )}
        {filteredGroups.map((group) => (
          <div key={group.name || '_flat'} className="mb-2 last:mb-0">
            {group.name && (
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 px-2.5 pt-1">
                {group.name}
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map((nodeType) => (
                <DraggableCard key={nodeType.type} nodeType={nodeType} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}
